import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Minus, Plus, ArrowLeft } from 'lucide-react';

import useProduct from '../hooks/useProduct';
import ProductGallery from '../components/product/ProductGallery';
import RelatedProducts from '../components/product/RelatedProducts';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import { useCart } from '../context/CartContext';
import { useToast } from '../components/ui/Toast';
import { formatPrice } from '../utils/money';

export default function ProductDetail() {
  const { id } = useParams();
  const { product, loading, error } = useProduct(id);
  const { addItem } = useCart();
  const { addToast } = useToast();
  const [qty, setQty] = useState(1);

  if (loading) {
    return (
      <div className="ss-container ss-state">
        <Spinner label="Loading product" size={32} />
      </div>
    );
  }

  // GET /product/:id is shared with the admin editor, so the server doesn't
  // filter it by category.active — the storefront hides it here instead.
  if (error || !product || product.category?.active === false) {
    return (
      <section className="ss-container ss-state">
        <h2>Product not found</h2>
        <p>This product may have been removed.</p>
        <p style={{ marginTop: 'var(--ss-space-4)' }}>
          <Link className="ss-btn ss-btn--primary" to="/products">Back to shop</Link>
        </p>
      </section>
    );
  }

  const category = product.category;
  const depositPercent = category?.depositPercent || 0;
  const depositAmount = depositPercent > 0 ? Math.round((product.price * depositPercent) / 100) : 0;
  const localPickupOnly = !!category?.localPickupOnly;

  const addToCart = () => {
    addItem(product, qty);
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/products/${product._id || product.id}`;
    try {
      await navigator.clipboard.writeText(url);
      addToast('Link copied!', { variant: 'success' });
    } catch {
      addToast('Could not copy link', { variant: 'error' });
    }
  };

  return (
    <section className="ss-container" style={{ paddingBlock: 'var(--ss-space-6)' }}>
      <p style={{ marginBottom: 'var(--ss-space-4)' }}>
        <Link to="/products" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <ArrowLeft size={16} aria-hidden="true" /> Back to shop
        </Link>
      </p>

      <div className="ss-detail">
        <ProductGallery photos={product.photos} title={product.title} onCopyLink={copyLink} />

        <div>
          {category?.name && (
            <Link to={`/category/${category.slug}`} className="ss-product-card__cat">
              {category.name}
            </Link>
          )}
          <h1 className="ss-detail__title">{product.title}</h1>
          <p className="ss-price ss-detail__price">{formatPrice(product.price)}</p>
          {depositPercent > 0 && (
            <p className="ss-detail__deposit">
              {depositPercent}% deposit ({formatPrice(depositAmount)}) due now, rest due later.
            </p>
          )}
          {localPickupOnly && (
            <p className="ss-detail__pickup">
              <Badge className="ss-pickup-badge">Local pickup only</Badge>
              This item can't be shipped — it must be picked up in person.
            </p>
          )}
          <p className="ss-detail__desc">{product.description}</p>

          <div style={{ display: 'flex', gap: 'var(--ss-space-4)', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="ss-qty" role="group" aria-label="Quantity">
              <button type="button" onClick={() => setQty((n) => Math.max(1, n - 1))} disabled={qty <= 1} aria-label="Decrease quantity">
                <Minus size={16} aria-hidden="true" />
              </button>
              <span aria-live="polite">{qty}</span>
              <button type="button" onClick={() => setQty((n) => n + 1)} aria-label="Increase quantity">
                <Plus size={16} aria-hidden="true" />
              </button>
            </div>
            <Button onClick={addToCart}>Add to cart</Button>
          </div>
        </div>
      </div>

      {category?.slug && (
        <RelatedProducts categorySlug={category.slug} excludeId={product._id || product.id} />
      )}
    </section>
  );
}
